import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { loading } from "../../redux/actions/loadingAction";
import { Row, Col } from "react-bootstrap";
import pdfMake from "pdfmake-thai/build/pdfmake";
import pdfFonts from "pdfmake-thai/build/vfs_fonts";
import BoxCard from "../../components/BoxCard";
import InputSelect from "../../components/InputSelect";
import InputDatePicker from "../../components/InputDatePicker";
import DataTableNodata from "../../components/DataTableNodata";
import Breadcrumb from "../../components/Breadcrumb";
import * as helper from "../../utils/helper";
import * as API from "../../utils/apis";
import * as FileSaver from "file-saver";
import * as XLSX from "xlsx";
import { useMasterData } from "../../components/hooks/useMasterData";

pdfMake.vfs = pdfFonts.pdfMake.vfs;

const REPORT_TITLE = "B99RPT06-รายงานการฉีดวัคซีน (กคร.5) รายอำเภอ";

// คอลัมน์ตัวเลขของรายงาน (ใช้ทั้งตาราง, excel, csv, pdf)
const amtColumns = [
  { key: "fmAmt", name: "จำนวน กคร.5" },
  { key: "farmAmt", name: "จำนวนฟาร์ม" },
  { key: "totalAmt", name: "จำนวนสัตว์ทั้งหมด (ร่วมฝูง)" },
  { key: "injectStaffAmt", name: "ฉีดโดย จนท.", group: "vaccine" },
  { key: "injectOwnerAmt", name: "ฉีดโดยเจ้าของ", group: "vaccine" },
  { key: "vaccineAmt", name: "รวมฉีด", group: "vaccine" },
  { key: "vaccinePercent", name: "ร้อยละที่ฉีด", group: "vaccine" },
  { key: "pregnantAmt", name: "ท้อง", group: "notVaccine" },
  { key: "less4monthAmt", name: "< 4 เดือน", group: "notVaccine" },
  { key: "notCatchAmt", name: "จับไม่ได้", group: "notVaccine" },
  { key: "otherAmt", name: "อื่นๆ", group: "notVaccine" },
];

const groupName = {
  vaccine: "จำนวนสัตว์ที่ฉีดวัคซีน (ตัว)",
  notVaccine: "จำนวนสัตว์ที่ไม่ได้รับวัคซีน (ตัว)",
};

const areaColumns = [
  "ลำดับ",
  "จังหวัด",
  "อำเภอ",
  "ตำบล",
  "ชนิดสัตว์",
  "ชนิดวัคซีน",
];

const formatAmt = (value) =>
  value || value === 0 ? Number(value).toLocaleString() : "-";

const getPercent = (item) =>
  item.totalAmt > 0
    ? ((item.vaccineAmt / item.totalAmt) * 100).toFixed(2)
    : "-";

const getAmtValue = (item, key) =>
  key === "vaccinePercent" ? getPercent(item) : formatAmt(item[key]);

function Vaccinate_Report() {
  const dispatch = useDispatch();
  const user = useSelector((state) => state.login.result);
  const { t } = useTranslation(["common", "footer"]);

  const initialForm = {
    zoneId: "",
    provinceId: "",
    amphurId: "",
    tambonId: "",
    animalTypeId: "",
    vaccineTypeId: "",
    dateBegin: "",
    dateEnd: "",
  };
  const [form, setForm] = useState(initialForm);

  const [report, setReport] = useState({
    content: [],
    amphurs: [],
    total: null,
  });

  // select เขต จังหวัด อำเภอ ตำบล
  const [zoneLivestockList, setZoneLivestockList] = useState([]);
  const [zoneLivestockSelectValue, setZoneLivestockSelectValue] = useState([]);

  const [provinceSelectValue, setProvinceSelectValue] = useState([]);
  const [provinceList, setProvinceList] = useState([]);

  const [districtSelectValue, setDistrictSelectValue] = useState([]);
  const [districtList, setDistrictList] = useState([]);

  const [subDistrictSelectValue, setSubDistrictSelectValue] = useState([]);
  const [subDistrictList, setSubDistrictList] = useState([]);

  // select ชนิดสัตว์ ชนิดวัคซีน
  const [animalSelectValue, setAnimalSelectValue] = useState([]);
  const [animalList, setAnimalList] = useState([]);

  const [vaccineSelectValue, setVaccineSelectValue] = useState([]);
  const [vaccineList, setVaccineList] = useState([]);

  const [fileType, setFileType] = useState("excel");
  const fileName = "B99RPT06-Vaccinate" + helper.getDate(new Date());

  const [isClearSearch, setIsClearSearch] = useState(false);

  const [provinceByZoneId, setProvinceByZoneId] = useState(null);
  useMasterData("province", provinceList, setProvinceList, provinceByZoneId);
  useMasterData(
    "zoneLivestock",
    zoneLivestockList,
    setZoneLivestockList,
    provinceByZoneId
  );

  const breadcrumbPath = [
    { path: "/home", name: "หน้าหลัก" },
    { path: "/passive_surv/disease_report", name: "การเฝ้าระวังเชิงรับ" },
    { path: "title", name: "รายงาน" },
    { path: "active", name: REPORT_TITLE },
  ];

  pdfMake.fonts = {
    THSarabunNew: {
      normal: "THSarabunNew.ttf",
      bold: "THSarabunNew-Bold.ttf",
      italics: "THSarabunNew-Italic.ttf",
      bolditalics: "THSarabunNew-BoldItalic.ttf",
    },
  };

  useEffect(() => {
    getAnimal();
    getVaccineType("");
  }, []);

  useEffect(() => {
    getVaccinateReport();
  }, [isClearSearch]);

  const getVaccinateReport = async () => {
    dispatch(loading(true));
    try {
      const params = {
        zoneId: form.zoneId,
        provinceId: form.provinceId,
        amphurId: form.amphurId,
        tambonId: form.tambonId,
        animalTypeId: form.animalTypeId,
        vaccineTypeId: form.vaccineTypeId,
        dateBegin: form.dateBegin ? helper.getDate(form.dateBegin) : "",
        dateEnd: form.dateEnd ? helper.getDate(form.dateEnd) : "",
      };
      const response = await API.getVaccinateReport(params);
      const data = await response?.data;
      if (response.status == 200) {
        setReport({
          content: data.content || [],
          amphurs: data.amphurs || [],
          total: data.total,
        });
      }
    } catch (error) {
      console.log(error);
    }
  };

  const getAnimal = async () => {
    try {
      const response = await API.getAnimal("", 1);
      const data = await response.data;
      if (response.status == 200) {
        let animalArray = [];
        data.content.forEach((element) => {
          animalArray.push({
            value: element.id,
            label: element.name,
          });
        });
        setAnimalList(animalArray);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const getVaccineType = async (animalTypeId) => {
    try {
      const response = await API.getVaccineType(animalTypeId);
      const data = await response.data;
      if (response.status == 200) {
        let vaccineArray = [];
        data.content.forEach((element) => {
          vaccineArray.push({
            value: element.id,
            label: element.name,
          });
        });
        setVaccineList(vaccineArray);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const getProvince = async (countyId) => {
    try {
      const req = {
        id: "",
        statusId: 1,
        page: "1",
        limit: "100",
        search: "",
        sortBy: "id",
        sortDir: "ASC",
        zoneId: countyId,
      };
      const response = await API.getProvinces(req);
      const data = await response.data;
      if (response.status == 200) {
        let provincesArray = [];
        data.content.forEach((element) => {
          provincesArray.push({
            value: element.id,
            label: element.name,
          });
        });
        setProvinceByZoneId(provincesArray);
        setProvinceList(provincesArray);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const getDistricts = async (id) => {
    try {
      const response = await API.getDistricts("", id, 1);
      const data = await response.data;
      if (response.status == 200) {
        let districtArray = [];
        data.content.forEach((element) => {
          districtArray.push({
            value: element.id,
            label: element.name,
          });
        });
        setDistrictList(districtArray);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const getSubDistricts = async (id) => {
    try {
      const response = await API.getSubDistricts("", id, 1);
      const data = await response.data;
      if (response.status === 200) {
        let subDistrictArray = [];
        data.content.forEach((element) => {
          subDistrictArray.push({
            value: element.id,
            label: element.name,
          });
        });
        setSubDistrictList(subDistrictArray);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const selectValueZone = (value) => {
    setZoneLivestockSelectValue([value]);
    setProvinceSelectValue([]);
    setDistrictSelectValue([]);
    setDistrictList([]);
    setSubDistrictSelectValue([]);
    setSubDistrictList([]);
    setForm({
      ...form,
      zoneId: value.value,
      provinceId: "",
      amphurId: "",
      tambonId: "",
    });
    getProvince(value.value);
  };

  const selectValueProvince = (value) => {
    setProvinceSelectValue([{ value: value.value, label: value.label }]);
    setDistrictSelectValue([]);
    setSubDistrictSelectValue([]);
    setSubDistrictList([]);
    setForm({ ...form, provinceId: value.value, amphurId: "", tambonId: "" });
    getDistricts(value.value);
  };

  const selectValueDistrict = (value) => {
    setDistrictSelectValue([{ value: value.value, label: value.label }]);
    setSubDistrictSelectValue([]);
    setSubDistrictList([]);
    setForm({ ...form, amphurId: value.value, tambonId: "" });
    getSubDistricts(value.value);
  };

  const selectValueSubDistrict = (value) => {
    setSubDistrictSelectValue([{ value: value.value, label: value.label }]);
    setForm({ ...form, tambonId: value.value });
  };

  const selectValueAnimal = (value) => {
    setAnimalSelectValue([value]);
    setVaccineSelectValue([]);
    setForm({ ...form, animalTypeId: value.value, vaccineTypeId: "" });
    getVaccineType(value.value);
  };

  const toSearch = () => {
    setIsClearSearch(!isClearSearch);
  };

  const clearSearch = () => {
    setForm(initialForm);
    setZoneLivestockSelectValue([]);
    setProvinceSelectValue([]);
    setDistrictSelectValue([]);
    setDistrictList([]);
    setSubDistrictSelectValue([]);
    setSubDistrictList([]);
    setAnimalSelectValue([]);
    setVaccineSelectValue([]);
    getVaccineType("");
    setFileType("excel");
    setProvinceByZoneId(null);
    setIsClearSearch(!isClearSearch);
  };

  // รวมแถวรายตำบล + แถวรวมรายอำเภอ + แถวรวมทั้งหมด
  const getReportRows = () => {
    let rows = [];
    let no = 0;
    report.amphurs.forEach((amphur) => {
      report.content
        .filter((item) => item.amphurId === amphur.amphurId)
        .forEach((item) => {
          no++;
          rows.push({ ...item, rowType: "detail", no: no });
        });
      rows.push({ ...amphur, rowType: "amphur" });
    });
    if (report.total && report.content.length > 0) {
      rows.push({ ...report.total, rowType: "total" });
    }
    return rows;
  };

  const getAreaValues = (item) => {
    if (item.rowType === "amphur") {
      return [
        "",
        item.provinceName,
        `รวมอำเภอ${item.amphurName || "-"}`,
        "",
        "",
        "",
      ];
    }
    if (item.rowType === "total") {
      return ["", "รวมทั้งหมด", "", "", "", ""];
    }
    return [
      item.no,
      item.provinceName || "-",
      item.amphurName || "-",
      item.tambonName || "-",
      item.animalTypeName || "-",
      item.vaccineTypeName || "-",
    ];
  };

  const getRowValues = (item) => [
    ...getAreaValues(item),
    ...amtColumns.map((col) => getAmtValue(item, col.key)),
  ];

  const getFlatHeader = () => [
    ...areaColumns,
    ...amtColumns.map((col) =>
      col.group ? `${groupName[col.group]} - ${col.name}` : col.name
    ),
  ];

  const getExportRows = () => [
    getFlatHeader(),
    ...getReportRows().map((item) => getRowValues(item)),
  ];

  const downloadRecord = () => {
    if (fileType === "excel") {
      const workSheet = XLSX.utils.aoa_to_sheet(getExportRows());
      const workBook = { Sheets: { data: workSheet }, SheetNames: ["data"] };
      const excelBuffer = XLSX.write(workBook, {
        bookType: "xlsx",
        type: "array",
      });
      const data = new Blob([excelBuffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8",
      });
      FileSaver.saveAs(data, fileName + ".xlsx");
    }
    if (fileType === "csv") {
      const csv = XLSX.utils.sheet_to_csv(
        XLSX.utils.aoa_to_sheet(getExportRows())
      );
      const csvData = new Blob(["﻿" + csv], {
        type: "text/csv;charset=utf-8",
      });
      FileSaver.saveAs(csvData, fileName + ".csv");
    }
    if (fileType === "pdf") {
      printPDF();
    }
  };

  const printPDF = () => {
    const headerCell = (text, opt = {}) => ({
      text: text,
      style: "tableHeader",
      alignment: "center",
      bold: true,
      ...opt,
    });

    let headRow1 = areaColumns.map((name) => headerCell(name, { rowSpan: 2 }));
    let headRow2 = areaColumns.map(() => ({}));
    amtColumns.forEach((col, i) => {
      if (!col.group) {
        headRow1.push(headerCell(col.name, { rowSpan: 2 }));
        headRow2.push({});
      } else {
        const isFirst = amtColumns[i - 1]?.group !== col.group;
        headRow1.push(
          isFirst
            ? headerCell(groupName[col.group], {
                colSpan: amtColumns.filter((c) => c.group === col.group)
                  .length,
              })
            : {}
        );
        headRow2.push(headerCell(col.name));
      }
    });

    let bodyArray = [headRow1, headRow2];
    getReportRows().forEach((item) => {
      bodyArray.push(
        getRowValues(item).map((value) => ({
          text: value === "" || value === null ? "" : String(value),
          bold: item.rowType !== "detail",
          fillColor: item.rowType !== "detail" ? "#eeeeee" : null,
        }))
      );
    });

    const docDefinition = {
      pageOrientation: "landscape",
      pageMargins: [10, 10, 10, 10],
      content: [
        { text: REPORT_TITLE, bold: true, fontSize: 14, margin: [0, 0, 0, 5] },
        {
          table: {
            widths: bodyArray[0].map(() => "auto"),
            headerRows: 2,
            body: bodyArray,
          },
        },
      ],
      defaultStyle: {
        fontSize: 9,
        font: "THSarabunNew",
      },
    };
    pdfMake.createPdf(docDefinition).download(fileName + ".pdf");
  };

  const checkExport = (value) => fileType === value;

  const buttonSearch = () => {
    return (
      <>
        <button className="btn btn-danger mr-2" onClick={() => clearSearch()}>
          ล้างข้อมูล
        </button>
        <button className="btn btn-success" onClick={() => toSearch()}>
          {t("search")}
        </button>
      </>
    );
  };

  const buttonExport = () => {
    return (
      <button
        className="btn btn-report"
        onClick={() => downloadRecord()}
        disabled={
          user?.userGroup.mexport === "n" || report.content.length === 0
        }
      >
        <a className="text-white">
          <img className="nav-icon" src={"/svg/report.svg"} alt="export-icon" />{" "}
          ออกรายงาน
        </a>
      </button>
    );
  };

  const renderHeader = () => {
    return (
      <>
        <tr>
          {areaColumns.map((name, i) => (
            <th key={i} rowSpan={2} className="text-center align-middle">
              {name}
            </th>
          ))}
          {amtColumns.map((col, i) => {
            if (!col.group) {
              return (
                <th key={col.key} rowSpan={2} className="text-center align-middle">
                  {col.name}
                </th>
              );
            }
            if (amtColumns[i - 1]?.group === col.group) return null;
            return (
              <th
                key={col.key}
                colSpan={amtColumns.filter((c) => c.group === col.group).length}
                className="text-center"
              >
                {groupName[col.group]}
              </th>
            );
          })}
        </tr>
        <tr>
          {amtColumns
            .filter((col) => col.group)
            .map((col) => (
              <th key={col.key} className="text-center">
                {col.name}
              </th>
            ))}
        </tr>
      </>
    );
  };

  const renderTable = () => {
    const rows = getReportRows();
    if (report.content.length === 0) {
      return (
        <DataTableNodata
          colSpan={areaColumns.length + amtColumns.length}
        />
      );
    }
    return rows.map((item, i) => (
      <tr
        key={i}
        className={`font14 fitwidth ${
          item.rowType !== "detail" ? "font-weight-bold" : ""
        }`}
        style={
          item.rowType === "total"
            ? { backgroundColor: "#d9e7f5" }
            : item.rowType === "amphur"
            ? { backgroundColor: "#eef3f8" }
            : {}
        }
      >
        {getAreaValues(item).map((value, j) => (
          <td key={j}>{value}</td>
        ))}
        {amtColumns.map((col) => (
          <td key={col.key} className="text-right">
            {getAmtValue(item, col.key)}
          </td>
        ))}
      </tr>
    ));
  };

  return (
    <div className="">
      <div className="pl-4 bg-white ml30">
        <Breadcrumb data={breadcrumbPath} />
      </div>

      <div className="bg-gray-light">
        <div className="px-4 pt-3 container-fixed-footer">
          <BoxCard title="ตัวกรอง" classLabel="" headRight={buttonSearch()}>
            <div className=" ">
              <Row className="mb-2 pl-2 ">
                <Col lg={2} md={6} sm={12} xs={12}>
                  <InputDatePicker
                    title="วันที่ฉีดวัคซีน"
                    classLabel="normal"
                    placeholder="วัน/เดือน/ปี"
                    value={form.dateBegin}
                    star={false}
                    handleChange={(value) =>
                      setForm({ ...form, dateBegin: value })
                    }
                  />
                </Col>

                <Col lg={2} md={6} sm={12} xs={12}>
                  <InputDatePicker
                    title="ถึงวันที่"
                    classLabel="normal"
                    placeholder="วัน/เดือน/ปี"
                    value={form.dateEnd}
                    star={false}
                    handleChange={(value) =>
                      setForm({ ...form, dateEnd: value })
                    }
                  />
                </Col>

                <Col lg={2} md={6} sm={12} xs={12}>
                  <InputSelect
                    title="ชนิดสัตว์"
                    star={false}
                    placeholder="กรุณาเลือก"
                    idName="animalType"
                    classLabel="normal"
                    selectValue={animalSelectValue}
                    optionsList={animalList}
                    handleChange={(value) => selectValueAnimal(value)}
                  />
                </Col>

                <Col lg={2} md={6} sm={12} xs={12}>
                  <InputSelect
                    title="ชนิดวัคซีน"
                    star={false}
                    placeholder="กรุณาเลือก"
                    idName="vaccineType"
                    classLabel="normal"
                    selectValue={vaccineSelectValue}
                    optionsList={vaccineList}
                    handleChange={(value) => {
                      setVaccineSelectValue([value]);
                      setForm({ ...form, vaccineTypeId: value.value });
                    }}
                  />
                </Col>
              </Row>

              <Row className="mb-2 pl-2  ">
                <Col lg={2} md={6} sm={12} xs={12}>
                  <InputSelect
                    title="เขตปศุสัตว์"
                    star={false}
                    placeholder="กรุณาเลือก"
                    idName="zone"
                    classLabel="normal"
                    selectValue={zoneLivestockSelectValue}
                    optionsList={zoneLivestockList}
                    handleChange={(value) => selectValueZone(value)}
                  />
                </Col>

                <Col lg={2} md={6} sm={12} xs={12}>
                  <InputSelect
                    title="จังหวัด"
                    star={false}
                    placeholder="กรุณาเลือก"
                    idName="province"
                    classLabel="normal"
                    selectValue={provinceSelectValue}
                    optionsList={provinceList}
                    handleChange={(value) => selectValueProvince(value)}
                  />
                </Col>

                <Col lg={2} md={6} sm={12} xs={12}>
                  <InputSelect
                    title="อำเภอ"
                    star={false}
                    placeholder="กรุณาเลือก"
                    idName="district"
                    classLabel="normal"
                    selectValue={districtSelectValue}
                    optionsList={districtList}
                    handleChange={(value) => selectValueDistrict(value)}
                  />
                </Col>

                <Col lg={2} md={6} sm={12} xs={12}>
                  <InputSelect
                    title="ตำบล"
                    star={false}
                    placeholder="กรุณาเลือก"
                    idName="subDistrict"
                    classLabel="normal"
                    selectValue={subDistrictSelectValue}
                    optionsList={subDistrictList}
                    handleChange={(value) => selectValueSubDistrict(value)}
                  />
                </Col>
              </Row>

              <div className="mb-2" style={{ fontWeight: "normal" }}>
                เลือกรูปแบบ
              </div>
              <Row>
                <Col lg={1} md={6} sm={12} xs={12}>
                  <label style={{ fontWeight: "normal" }}>
                    <input
                      type="radio"
                      value="excel"
                      name="data-export"
                      checked={checkExport("excel")}
                      onChange={() => setFileType("excel")}
                    />{" "}
                    EXCEL
                  </label>
                </Col>

                <Col lg={1} md={6} sm={12} xs={12}>
                  <label style={{ fontWeight: "normal" }}>
                    <input
                      type="radio"
                      value="pdf"
                      name="data-export"
                      checked={checkExport("pdf")}
                      onChange={() => setFileType("pdf")}
                    />{" "}
                    PDF
                  </label>
                </Col>

                <Col lg={1} md={6} sm={12} xs={12}>
                  <label style={{ fontWeight: "normal" }}>
                    <input
                      type="radio"
                      value="csv"
                      name="data-export"
                      checked={checkExport("csv")}
                      onChange={() => setFileType("csv")}
                    />{" "}
                    CSV
                  </label>
                </Col>
              </Row>
            </div>
          </BoxCard>

          <BoxCard
            classNameBox="mt-3"
            title={`รายงาน ${REPORT_TITLE}`}
            headRight={buttonExport()}
          >
            <div className="table-responsive">
              <table className="table table-striped table-bordered my-3">
                <thead>{renderHeader()}</thead>
                <tbody>{renderTable()}</tbody>
              </table>
            </div>
          </BoxCard>
        </div>
      </div>
    </div>
  );
}

export default Vaccinate_Report;
